import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { subscriptionKeys } from '../hooks/queryKeys';

/**
 * Subscription reports (spec D15/D16): recurring revenue, 12-month renewal
 * calendar and exceptions (with the QuickBooks checks), from
 * GET /v4/subscriptions/reports/*. Loads only once opened: the exceptions report
 * reads QuickBooks.
 */

interface RevenueRow {
  currency: string;
  verified: number;
  unverified: number;
  subscriptions: number;
}
interface AccountRevenue extends RevenueRow { accountId: string; accountNumber: string; accountName: string }
interface ManagerRevenue extends RevenueRow { salesManagerId: string | null; salesManagerName: string | null }
interface RecurringRevenueReport { byAccount: AccountRevenue[]; bySalesManager: ManagerRevenue[] }

interface CalendarEvent {
  subscriptionId: string;
  subscriptionNumber: string;
  accountNumber: string;
  accountName: string;
  type: 'billing' | 'end';
  date: string;
  overdue: boolean;
}
interface CalendarReport { events: CalendarEvent[] }

interface ExceptionRow {
  subscriptionNumber: string | null;
  accountNumber: string;
  accountName: string;
  reason: string;
  invoiceNumber?: string;
  [detail: string]: unknown;
}
interface ExceptionsReport {
  exceptions: ExceptionRow[];
  quickbooks: { status: 'checked' | 'unchecked'; error?: string; checked: number };
}

const REASON_LABELS: Record<string, string> = {
  no_lines: 'No lines',
  unverified_lines: 'Unverified lines',
  renewal_due_no_invoice: 'Renewal due, no invoice for the period',
  ceiling_warning: 'Ceiling ≥ 80 %',
  ceiling_unknown: 'Ceiling unknown',
  ending_soon: 'Contract ending within notice',
  sensors_no_subscription: 'Sensors with no subscription',
  covered_by_multiple: 'Covered by more than one subscription',
  qb_missing: 'Missing in QuickBooks',
  qb_duplicate_number: 'Two QuickBooks invoices for one number',
  qb_service_date_mismatch: 'QuickBooks ServiceDate ≠ period start',
};

function useReport<T>(name: string, enabled: boolean) {
  return useQuery({
    queryKey: [...subscriptionKeys.all, 'reports', name],
    queryFn: () => api.get<T>(`/v4/subscriptions/reports/${name}`),
    enabled,
  });
}

function exceptionDetail(row: ExceptionRow): string {
  if (row.reason === 'ceiling_warning') return `${(row.ceiling as { pct?: number } | undefined)?.pct ?? ''} %`;
  if (row.reason === 'ending_soon') return `${row.endDate} (${row.daysLeft} d)`;
  if (row.reason === 'unverified_lines' || row.reason === 'sensors_no_subscription') return String(row.count ?? '');
  if (row.reason === 'covered_by_multiple') return ((row.subscriptionNumbers as string[]) || []).join(', ');
  if (row.reason === 'renewal_due_no_invoice') return String(row.billingDate ?? '');
  if (row.reason === 'qb_duplicate_number') return ((row.quickbooksInvoiceIds as string[]) || []).join(', ');
  if (row.reason === 'qb_service_date_mismatch') return `${row.periodStart} vs ${((row.serviceDates as string[]) || []).join(', ')}`;
  return '';
}

function Section({ title, loading, children }: { title: string; loading: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      {loading ? <Skeleton className="h-24 w-full" /> : children}
    </div>
  );
}

function RevenueTable({ rows, label, keyOf, nameOf }: {
  rows: RevenueRow[];
  label: string;
  keyOf: (row: RevenueRow) => string;
  nameOf: (row: RevenueRow) => string;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{label}</TableHead>
          <TableHead className="text-right">Verified / year</TableHead>
          <TableHead className="text-right">Unverified / year</TableHead>
          <TableHead className="text-right">Subscriptions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={keyOf(row)} data-testid={keyOf(row)}>
            <TableCell>{nameOf(row)}</TableCell>
            <TableCell className="text-right tabular-nums" data-col="verified">{formatCurrency(row.verified, row.currency)}</TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground" data-col="unverified">{formatCurrency(row.unverified, row.currency)}</TableCell>
            <TableCell className="text-right tabular-nums">{row.subscriptions}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function SubscriptionReports() {
  const { t } = useTranslation('subscriptions');
  const [open, setOpen] = useState(false);
  const revenue = useReport<RecurringRevenueReport>('recurring-revenue', open);
  const calendar = useReport<CalendarReport>('calendar', open);
  const exceptions = useReport<ExceptionsReport>('exceptions', open);

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4" data-testid="subscription-reports">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('reports.title', 'Reports')}</h2>
        <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)} data-testid="subscription-reports-toggle">
          {open ? t('reports.hide', 'Hide reports') : t('reports.show', 'Show reports')}
        </Button>
      </div>

      {open && (
        <div className="space-y-6">
          <Section title={t('reports.revenue', 'Recurring revenue')} loading={revenue.isLoading}>
            {revenue.data && (
              <div className="grid gap-4 lg:grid-cols-2">
                <RevenueTable
                  rows={revenue.data.byAccount}
                  label="Account"
                  keyOf={(r) => `revenue-account-${(r as AccountRevenue).accountId}`}
                  nameOf={(r) => `${(r as AccountRevenue).accountNumber} ${(r as AccountRevenue).accountName}`}
                />
                <RevenueTable
                  rows={revenue.data.bySalesManager}
                  label="Sales manager"
                  keyOf={(r) => `revenue-manager-${(r as ManagerRevenue).salesManagerId ?? 'none'}-${r.currency}`}
                  nameOf={(r) => (r as ManagerRevenue).salesManagerName ?? 'Unassigned'}
                />
              </div>
            )}
          </Section>

          <Section title={t('reports.calendar', 'Renewal calendar (12 months)')} loading={calendar.isLoading}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Account</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(calendar.data?.events ?? []).map((e, i) => (
                  <TableRow key={`${e.subscriptionId}-${e.type}`} data-testid={`calendar-row-${i}`}>
                    <TableCell className="tabular-nums">{formatDate(e.date)}</TableCell>
                    <TableCell>
                      {e.type === 'billing' ? 'Billing' : 'Contract end'}
                      {e.overdue && <span className="ml-2 text-danger">overdue</span>}
                    </TableCell>
                    <TableCell>{e.subscriptionNumber}</TableCell>
                    <TableCell>{`${e.accountNumber} ${e.accountName}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>

          <Section title={t('reports.exceptions', 'Exceptions')} loading={exceptions.isLoading}>
            {exceptions.data && (
              <>
                <p className="text-xs text-muted-foreground" data-testid="quickbooks-status">
                  {exceptions.data.quickbooks.status === 'checked'
                    ? `QuickBooks: ${exceptions.data.quickbooks.checked} renewal invoices checked`
                    : `QuickBooks: unchecked (${exceptions.data.quickbooks.error ?? 'unknown error'})`}
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reason</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Detail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {exceptions.data.exceptions.map((row, i) => (
                      <TableRow key={`${row.subscriptionNumber ?? row.accountNumber}-${row.reason}-${row.invoiceNumber ?? ''}`} data-testid={`exception-row-${i}`}>
                        <TableCell>{REASON_LABELS[row.reason] ?? row.reason}</TableCell>
                        <TableCell>{row.subscriptionNumber ?? '—'}</TableCell>
                        <TableCell>{`${row.accountNumber} ${row.accountName}`}</TableCell>
                        <TableCell>{row.invoiceNumber ?? ''}</TableCell>
                        <TableCell className="text-xs">{exceptionDetail(row)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}
